// SEQ-169 hire comments: the audit-notes modal, generalised from variations to
// any commentable entity. Variations and hire register items share one
// implementation and one pair of tables (claims_entity_comments and
// claims_entity_views) so the V2 ledger can join without another migration.
//
// This replaces VariationCommentsModal, which was generalised into this file and
// then removed rather than left as a wrapper. Its only importer, ProjectDetail,
// now mounts this component with entityType set to variation.

import { useState, useEffect, useCallback, useRef } from "react";
import { X, Send, Flag, Handshake, Users } from "lucide-react";
import { supabase, requireOrganizationId } from "../lib/supabase";
import { entityCommentsDb } from "../lib/entityCommentsDb";
import {
  ENTITY_COMMENTS_TABLE,
  ENTITY_FLAG_TABLE,
  ENTITY_VIEWS_TABLE,
  ENTITY_VIEW_CONFLICT_TARGET,
  normaliseFlag,
  type CommentEntityType,
  type CommentFlag,
} from "../lib/entityComments";
import { useToast } from "./ui/ToastModal";
import { useAuth } from "../hooks/useAuth";
import { logError } from "../lib/logError";

// ─────────────────────────────────────────────
// Mention support constants
// ─────────────────────────────────────────────
const MENTION_REGEX = /@\[(\w+):([^:]+):([^\]]+)\]/g;

interface MentionableUser {
  id: string;
  label: string;
  sublabel?: string;
}

/** Render comment text with mention markers as styled chips */
function renderCommentContent(content: string) {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  const mentionRegex = new RegExp(MENTION_REGEX.source, "g");

  while ((match = mentionRegex.exec(content)) !== null) {
    // Add text before mention
    if (match.index > lastIndex) {
      parts.push(
        <span key={`text-${parts.length}`}>
          {content.slice(lastIndex, match.index)}
        </span>
      );
    }

    // Add mention chip
    const [, , , label] = match;
    parts.push(
      <span
        key={`mention-${parts.length}`}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "3px",
          padding: "1px 6px",
          borderRadius: "4px",
          background: "rgba(20,184,166,0.1)",
          color: "#14b8a6",
          border: "1px solid rgba(20,184,166,0.25)",
          fontSize: "0.76rem",
          fontWeight: 600,
          verticalAlign: "middle",
          lineHeight: 1.6,
        }}
        title={`Team member: ${label}`}
      >
        <Users size={10} />
        @{label}
      </span>
    );
    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < content.length) {
    parts.push(
      <span key={`text-${parts.length}`}>{content.slice(lastIndex)}</span>
    );
  }

  return parts.length > 0 ? parts : content;
}

type FlagType = CommentFlag;

interface Comment {
  id: string;
  user_name: string;
  user_id?: string;
  comment: string;
  flag: FlagType;
  created_at: string;
}

interface View {
  user_id: string;
  last_read_at: string;
  user_name?: string;
  email?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Which table entityId points at. */
  entityType: CommentEntityType;
  /** Primary key of the variation or hire register item being annotated. */
  entityId: string;
  /** Human label for the header: a docket number, or the hire item name. */
  entityLabel: string;
  /**
   * Fired after a comment is posted so the caller can update the shared
   * needs-review dot without refetching.
   */
  onFlagChange?: (entityId: string, flag: FlagType) => void;
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2)
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (parts[0]?.[0] || "?").toUpperCase();
}

function timeAgo(dateStr: string): string {
  const now = new Date();
  const d = new Date(dateStr);
  const secs = Math.floor((now.getTime() - d.getTime()) / 1000);
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: d.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
}

export function EntityCommentsModal({
  isOpen,
  onClose,
  entityType,
  entityId,
  entityLabel,
  onFlagChange,
}: Props) {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [comments, setComments] = useState<Comment[]>([]);
  const [views, setViews] = useState<View[]>([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [selectedFlag, setSelectedFlag] = useState<FlagType>("green");
  const [submitting, setSubmitting] = useState(false);

  // Mention state
  const [mentionUsers, setMentionUsers] = useState<MentionableUser[]>([]);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionIndex, setMentionIndex] = useState(0);
  const editorRef = useRef<HTMLDivElement>(null);
  const mentionAnchorRange = useRef<Range | null>(null);

  const fetchComments = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    const { data: commentsData } = await entityCommentsDb
      .from(ENTITY_COMMENTS_TABLE)
      .select("*")
      .eq("entity_type", entityType)
      .eq("entity_id", entityId)
      .order("created_at", { ascending: true });
    setComments(
      (commentsData || []).map((row) => ({
        id: row.id,
        user_name: row.user_name,
        user_id: row.user_id,
        comment: row.comment,
        flag: normaliseFlag(row.flag),
        created_at: row.created_at,
      })),
    );

    // Fetch views for this entity
    const { data: viewsData, error: viewsError } = await entityCommentsDb
      .from(ENTITY_VIEWS_TABLE)
      .select("user_id, last_read_at")
      .eq("entity_type", entityType)
      .eq("entity_id", entityId);

    if (viewsError) {
      logError("Error fetching views:", viewsError);
    }

    if (viewsData && viewsData.length > 0) {
      // Map user profiles to views
      const userIds = viewsData.map(v => v.user_id);
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .in("id", userIds);

      const enhancedViews = viewsData.map(v => {
        const profile = profiles?.find(p => p.id === v.user_id);
        return {
          user_id: v.user_id,
          last_read_at: v.last_read_at,
          user_name: profile?.full_name || "Unknown",
          email: profile?.email || "",
        };
      });
      setViews(enhancedViews);
    } else {
      setViews([]);
    }

    setLoading(false);
  }, [entityId, entityType]);

  useEffect(() => {
    if (isOpen && user?.id && entityId) {
      // Mark as read first, then fetch comments + views
      const markReadAndFetch = async () => {
        await entityCommentsDb
          .from(ENTITY_VIEWS_TABLE)
          .upsert(
            {
              entity_type: entityType,
              entity_id: entityId,
              user_id: user.id,
              organization_id: requireOrganizationId(),
              last_read_at: new Date().toISOString(),
            },
            { onConflict: ENTITY_VIEW_CONFLICT_TARGET }
          );
        await fetchComments();
      };
      markReadAndFetch();
    }
  }, [isOpen, user?.id, entityId, entityType, fetchComments]);

  // Load mentionable team members
  const loadMentionUsers = useCallback(async () => {
    const orgId = requireOrganizationId();
    const { data: membersData } = await supabase
      .from("organization_members")
      .select(`
        user_id,
        role,
        profiles!organization_members_user_id_profiles_fkey (
          full_name,
          email
        )
      `)
      .eq("organization_id", orgId)
      .order("joined_at", { ascending: true });

    const seenUserIds = new Set<string>();
    const users: MentionableUser[] = [];
    (membersData || []).forEach((row: Record<string, unknown>) => {
      const uid = row.user_id as string;
      if (seenUserIds.has(uid)) return;
      seenUserIds.add(uid);
      const profile = row.profiles as Record<string, unknown> | null;
      if (profile?.full_name) {
        users.push({
          id: uid,
          label: profile.full_name as string,
          sublabel: (profile.email as string) || undefined,
        });
      }
    });
    // Sort team members alphabetically
    users.sort((a, b) => a.label.localeCompare(b.label));
    setMentionUsers(users);
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadMentionUsers();
    }
  }, [isOpen, loadMentionUsers]);

  // Filtered mention list based on query
  const filteredMentions = mentionUsers.filter(
    (u) =>
      u.label.toLowerCase().includes(mentionQuery.toLowerCase()) ||
      (u.sublabel && u.sublabel.toLowerCase().includes(mentionQuery.toLowerCase()))
  );

  // ─────────────────────────────
  // ContentEditable Rich Editor Helpers
  // ─────────────────────────────

  /** Walk the editor DOM and serialize to plain text with @[user:id:label] markup */
  const serializeEditor = (): string => {
    const editor = editorRef.current;
    if (!editor) return "";

    const walk = (node: Node): string => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent || "";
      }
      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        // Mention chip span
        if (el.dataset.mentionType) {
          return `@[${el.dataset.mentionType}:${el.dataset.mentionId}:${el.dataset.mentionLabel}]`;
        }
        // <br> → newline
        if (el.tagName === "BR") return "\n";
        // Recurse into other elements (div wraps = newlines in contentEditable)
        let result = "";
        el.childNodes.forEach((child) => {
          result += walk(child);
        });
        // Block elements like <div> insert a newline before their content
        if (el.tagName === "DIV" && el !== editor && el.previousSibling) {
          result = "\n" + result;
        }
        return result;
      }
      return "";
    };

    let result = "";
    editor.childNodes.forEach((child) => {
      result += walk(child);
    });
    return result;
  };

  /** Update the `text` state from editor content (for submit button disabled state) */
  const syncTextState = () => {
    setText(serializeEditor());
  };

  // Handle @-trigger detection in contentEditable
  const handleEditorInput = () => {
    syncTextState();

    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) {
      setShowMentionDropdown(false);
      mentionAnchorRange.current = null;
      return;
    }

    const range = sel.getRangeAt(0);
    const container = range.startContainer;

    // Only detect @ in text nodes
    if (container.nodeType !== Node.TEXT_NODE) {
      setShowMentionDropdown(false);
      mentionAnchorRange.current = null;
      return;
    }

    const textContent = container.textContent || "";
    const cursorOffset = range.startOffset;
    const textBeforeCursor = textContent.slice(0, cursorOffset);
    const lastAtIndex = textBeforeCursor.lastIndexOf("@");

    if (lastAtIndex !== -1) {
      const charBeforeAt = lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : " ";
      if (charBeforeAt === " " || charBeforeAt === "\n" || charBeforeAt === "\u00A0" || lastAtIndex === 0) {
        const textAfterAt = textBeforeCursor.slice(lastAtIndex + 1);
        const q = textAfterAt.toLowerCase();
        const hasMatches = mentionUsers.some(
          (u) =>
            u.label.toLowerCase().includes(q) ||
            (u.sublabel && u.sublabel.toLowerCase().includes(q))
        );
        if (hasMatches || textAfterAt.length === 0) {
          // Save the Range so we know where to insert the chip
          const anchorRange = document.createRange();
          anchorRange.setStart(container, lastAtIndex);
          anchorRange.setEnd(container, cursorOffset);
          mentionAnchorRange.current = anchorRange;

          setShowMentionDropdown(true);
          setMentionQuery(textAfterAt);
          setMentionIndex(0);
          return;
        }
      }
    }

    setShowMentionDropdown(false);
    mentionAnchorRange.current = null;
  };

  // Handle mention selection by inserting a DOM chip
  const handleMentionSelect = (mentionUser: MentionableUser) => {
    const editor = editorRef.current;
    const anchor = mentionAnchorRange.current;
    if (!editor || !anchor) return;

    // Create the chip span
    const chip = document.createElement("span");
    chip.contentEditable = "false";
    chip.dataset.mentionType = "user";
    chip.dataset.mentionId = mentionUser.id;
    chip.dataset.mentionLabel = mentionUser.label;
    chip.setAttribute("style", [
      "display:inline-flex",
      "align-items:center",
      "gap:3px",
      "padding:1px 8px",
      "border-radius:4px",
      "background:rgba(20,184,166,0.1)",
      "color:#14b8a6",
      "border:1px solid rgba(20,184,166,0.25)",
      "font-size:0.78rem",
      "font-weight:600",
      "cursor:default",
      "vertical-align:middle",
      "line-height:1.6",
      "user-select:none",
      "margin:0 1px",
    ].join(";"));

    // Icon SVG (the Users icon, two people)
    const iconSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    iconSvg.setAttribute("width", "11");
    iconSvg.setAttribute("height", "11");
    iconSvg.setAttribute("viewBox", "0 0 24 24");
    iconSvg.setAttribute("fill", "none");
    iconSvg.setAttribute("stroke", "currentColor");
    iconSvg.setAttribute("stroke-width", "2");
    iconSvg.setAttribute("stroke-linecap", "round");
    iconSvg.setAttribute("stroke-linejoin", "round");
    const iconPath = document.createElementNS("http://www.w3.org/2000/svg", "path");
    iconPath.setAttribute("d", "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2");
    iconSvg.appendChild(iconPath);
    const circle1 = document.createElementNS("http://www.w3.org/2000/svg", "circle");
    circle1.setAttribute("cx", "9"); circle1.setAttribute("cy", "7"); circle1.setAttribute("r", "4");
    iconSvg.appendChild(circle1);
    const path2 = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path2.setAttribute("d", "M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75");
    iconSvg.appendChild(path2);
    chip.appendChild(iconSvg);

    // Label text
    const labelText = document.createTextNode(`@${mentionUser.label}`);
    chip.appendChild(labelText);

    // Delete the "@query" text and insert the chip
    const sel = window.getSelection();
    if (sel) {
      sel.removeAllRanges();
      sel.addRange(anchor);
      anchor.deleteContents();
      anchor.insertNode(chip);

      // Insert a space after the chip and move cursor there
      const space = document.createTextNode("\u00A0");
      chip.after(space);

      // Move cursor after the space
      const newRange = document.createRange();
      newRange.setStartAfter(space);
      newRange.collapse(true);
      sel.removeAllRanges();
      sel.addRange(newRange);
    }

    mentionAnchorRange.current = null;
    setShowMentionDropdown(false);
    syncTextState();
  };

  // Handle keyboard nav in mention dropdown
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!showMentionDropdown || filteredMentions.length === 0) {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setMentionIndex((i) => Math.min(i + 1, filteredMentions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setMentionIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      handleMentionSelect(filteredMentions[mentionIndex]);
    } else if (e.key === "Escape") {
      setShowMentionDropdown(false);
    }
  };

  const handleSubmit = async () => {
    if (!text.trim() || !user) return;
    setSubmitting(true);
    try {
      // Get user profile for name
      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", user.id)
        .single();

      const userName = profile?.full_name || user.email || "Unknown";

      // Insert comment and RETURNING inserted row
      const { data: newComment, error } = await entityCommentsDb
        .from(ENTITY_COMMENTS_TABLE)
        .insert({
          entity_type: entityType,
          entity_id: entityId,
          user_id: user.id,
          user_name: userName,
          comment: text.trim(),
          flag: selectedFlag,
          organization_id: requireOrganizationId(),
        })
        .select()
        .single();

      if (error) throw error;

      // Denormalise the flag onto the entity so the shared needs-review dot
      // updates for everyone, not just the author.
      await entityCommentsDb
        .from(ENTITY_FLAG_TABLE[entityType])
        .update({ audit_flag: selectedFlag })
        .eq("id", entityId);

      // Notify the team. The notifier resolves the entity from the comment
      // itself, so variations and hire items both go through it.
      if (newComment) {
        // Send local formatted time context to backend
        const localTime = new Date().toLocaleString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });

        supabase.functions
          .invoke('notify-variation-comment', {
            body: {
              commentId: newComment.id,
              originUrl: window.location.origin,
              localTime
            }
          })
          .then(({ error: notifyError }) => {
            // supabase-js resolves rather than rejects on a non-2xx, so a bare
            // catch never fires and a broken notifier looks like a working one.
            // Rethrow so the handler below is the single place that reports it.
            if (notifyError) throw notifyError;
          })
          .catch((notifyError: unknown) => {
            // The note is saved either way. Say plainly that nobody was told,
            // rather than letting a silent failure pass for success.
            logError("Failed to notify the team about the audit note:", notifyError);
            addToast({
              type: "error",
              title: "Note saved, but the team was not notified",
            });
          });
      }

      setText("");
      // Clear the contentEditable editor
      if (editorRef.current) {
        editorRef.current.innerHTML = "";
      }

      // Posting a comment means you have seen everything, so advance the receipt
      await entityCommentsDb
        .from(ENTITY_VIEWS_TABLE)
        .upsert(
          {
            entity_type: entityType,
            entity_id: entityId,
            user_id: user.id,
            organization_id: requireOrganizationId(),
            last_read_at: new Date().toISOString(),
          },
          { onConflict: ENTITY_VIEW_CONFLICT_TARGET }
        );

      addToast({
        type: "success",
        title:
          selectedFlag === "red"
            ? "Flagged for review"
            : selectedFlag === "purple"
              ? "Agreement Reached noted"
              : "Comment added",
      });
      fetchComments();
      onFlagChange?.(entityId, selectedFlag);
    } catch {
      addToast({ type: "error", title: "Failed to add comment" });
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: "520px",
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <div
          className="modal-header"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h3
            className="modal-title"
            style={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            <Flag size={18} />
            {`Audit Notes: ${entityLabel}`}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "4px",
              color: "var(--text-secondary)",
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Comments list */}
        <div
          className="modal-body"
          style={{
            flex: 1,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            minHeight: "200px",
            maxHeight: "400px",
          }}
        >
          {loading ? (
            <div
              style={{
                textAlign: "center",
                color: "var(--text-muted)",
                padding: "32px 0",
              }}
            >
              Loading...
            </div>
          ) : comments.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                color: "var(--text-muted)",
                padding: "32px 0",
                fontSize: "0.85rem",
              }}
            >
              No audit notes yet. Add the first comment below.
            </div>
          ) : (
            comments.map((c, commentIndex) => {
              // Determine which other users have "seen up to" this comment
              // A user's avatar appears on the LAST comment created BEFORE their last_read_at
              const otherViews = views.filter(v => v.user_id !== user?.id);
              const seenByHere = otherViews.filter(v => {
                const lastRead = new Date(v.last_read_at).getTime();
                const thisCommentTime = new Date(c.created_at).getTime();
                // This comment must be within their read window
                if (thisCommentTime > lastRead) return false;
                // Check for a LATER comment they have also read; show the avatar there instead
                const nextComment = comments[commentIndex + 1];
                if (nextComment) {
                  const nextTime = new Date(nextComment.created_at).getTime();
                  if (nextTime <= lastRead) return false; // They've read further, skip this one
                }
                return true;
              });

              return (
                <div key={c.id}>
                  {/* Comment bubble */}
                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                      padding: "10px 12px",
                      background:
                        c.flag === "red"
                          ? "rgba(239, 68, 68, 0.06)"
                          : c.flag === "purple"
                            ? "rgba(147, 51, 234, 0.06)"
                            : "rgba(34, 197, 94, 0.06)",
                      borderRadius: "8px",
                      borderLeft: `3px solid ${
                        c.flag === "red"
                          ? "#ef4444"
                          : c.flag === "purple"
                            ? "#9333ea"
                            : "#22c55e"
                      }`,
                    }}
                  >
                    {/* Avatar */}
                    <div
                      style={{
                        width: "32px",
                        height: "32px",
                        borderRadius: "50%",
                        background:
                          c.flag === "red"
                            ? "linear-gradient(135deg, #fca5a5, #ef4444)"
                            : c.flag === "purple"
                              ? "linear-gradient(135deg, #c4b5fd, #9333ea)"
                              : "linear-gradient(135deg, #86efac, #22c55e)",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        flexShrink: 0,
                        letterSpacing: "0.02em",
                      }}
                    >
                      {getInitials(c.user_name)}
                    </div>

                    {/* Content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginBottom: "4px",
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 600,
                            fontSize: "0.8rem",
                            color: "var(--text-primary)",
                          }}
                        >
                          {c.user_name.split(" ")[0]}
                        </span>
                        <span
                          style={{
                            fontSize: "0.7rem",
                            color: "var(--text-muted)",
                          }}
                        >
                          {timeAgo(c.created_at)}
                        </span>
                        <span
                          style={{
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            background:
                              c.flag === "red"
                                ? "#ef4444"
                                : c.flag === "purple"
                                  ? "#9333ea"
                                  : "#22c55e",
                            flexShrink: 0,
                          }}
                        />
                      </div>
                      <div
                        style={{
                          margin: 0,
                          fontSize: "0.82rem",
                          color: "var(--text-secondary)",
                          lineHeight: 1.5,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {renderCommentContent(c.comment)}
                      </div>
                    </div>
                  </div>

                  {/* Per-comment seen-by (Messenger style) */}
                  {seenByHere.length > 0 && (
                    <div style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      padding: "3px 8px 2px",
                    }}>
                      <div style={{ display: "flex" }}>
                        {seenByHere.map((v, i) => (
                          <div
                            key={v.user_id}
                            style={{
                              position: "relative",
                              marginLeft: i === 0 ? 0 : "-4px",
                              zIndex: seenByHere.length - i,
                            }}
                          >
                            <div
                              style={{
                                width: "16px",
                                height: "16px",
                                borderRadius: "50%",
                                background: "linear-gradient(135deg, #a5b4fc, #6366f1)",
                                color: "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "0.45rem",
                                fontWeight: 700,
                                border: "1.5px solid var(--bg-surface, #fff)",
                                cursor: "pointer",
                                boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                                transition: "transform 0.15s ease",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "scale(1.2)";
                                const tooltip = e.currentTarget.parentElement?.querySelector(".seen-tooltip") as HTMLElement;
                                if (tooltip) { tooltip.style.opacity = "1"; tooltip.style.pointerEvents = "auto"; }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "scale(1)";
                                const tooltip = e.currentTarget.parentElement?.querySelector(".seen-tooltip") as HTMLElement;
                                if (tooltip) { tooltip.style.opacity = "0"; tooltip.style.pointerEvents = "none"; }
                              }}
                            >
                              {getInitials(v.user_name || "U")}
                            </div>

                            {/* Hover tooltip card */}
                            <div
                              className="seen-tooltip"
                              style={{
                                position: "absolute",
                                bottom: "calc(100% + 6px)",
                                right: 0,
                                background: "var(--bg-surface, #fff)",
                                border: "1px solid var(--border-color-light, #e2e8f0)",
                                borderRadius: "10px",
                                padding: "8px 12px",
                                boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                                opacity: 0,
                                pointerEvents: "none",
                                transition: "opacity 0.15s ease",
                                whiteSpace: "nowrap",
                                zIndex: 100,
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                minWidth: "160px",
                              }}
                            >
                              <div style={{
                                width: "26px",
                                height: "26px",
                                borderRadius: "50%",
                                background: "linear-gradient(135deg, #a5b4fc, #6366f1)",
                                color: "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "0.6rem",
                                fontWeight: 700,
                                flexShrink: 0,
                              }}>
                                {getInitials(v.user_name || "U")}
                              </div>
                              <div style={{ display: "flex", flexDirection: "column", gap: "1px" }}>
                                <span style={{
                                  fontWeight: 600,
                                  fontSize: "0.75rem",
                                  color: "var(--text-primary)",
                                }}>
                                  {v.user_name}
                                </span>
                                {v.email && (
                                  <span style={{
                                    fontSize: "0.65rem",
                                    color: "var(--text-muted)",
                                  }}>
                                    {v.email}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Add comment form */}
        <div
          style={{
            borderTop: "1px solid var(--border-color-light)",
            padding: "14px 20px",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          {/* Mention dropdown */}
          {showMentionDropdown && filteredMentions.length > 0 && (
            <div
              style={{
                background: "var(--bg-surface, #fff)",
                border: "1px solid var(--border-color-light)",
                borderRadius: "10px",
                boxShadow: "0 -4px 20px rgba(0,0,0,0.12)",
                padding: "4px",
                maxHeight: "160px",
                overflowY: "auto",
              }}
            >
              <div
                style={{
                  padding: "6px 10px 3px",
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  color: "var(--text-muted)",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <Users size={10} />
                Team Members
              </div>
              {filteredMentions.map((mu, idx) => (
                <div
                  key={mu.id}
                  onClick={() => handleMentionSelect(mu)}
                  onMouseEnter={() => setMentionIndex(idx)}
                  style={{
                    padding: "6px 10px",
                    borderRadius: "6px",
                    cursor: "pointer",
                    background:
                      idx === mentionIndex
                        ? "rgba(20,184,166,0.08)"
                        : "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    transition: "background 0.1s",
                  }}
                >
                  <span
                    style={{
                      fontSize: "0.82rem",
                      fontWeight: idx === mentionIndex ? 600 : 400,
                      color: "var(--text-primary)",
                    }}
                  >
                    {mu.label}
                  </span>
                  {mu.sublabel && (
                    <span
                      style={{
                        fontSize: "0.7rem",
                        color: "var(--text-muted)",
                      }}
                    >
                      {mu.sublabel}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Rich editor with @mention chips */}
          <div
            ref={editorRef}
            contentEditable
            onInput={handleEditorInput}
            onKeyDown={handleKeyDown}
            role="textbox"
            aria-multiline="true"
            aria-label="Add an audit note"
            data-placeholder="Add an audit note… Use @ to mention team members"
            className="form-input notes-rich-editor"
            style={{
              fontSize: "0.85rem",
              minHeight: "52px",
              maxHeight: "120px",
              overflowY: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              outline: "none",
              cursor: "text",
            }}
          />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {/* Flag selector */}
            <div style={{ display: "flex", gap: "6px" }}>
              <button
                type="button"
                onClick={() => setSelectedFlag("green")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  padding: "5px 12px",
                  borderRadius: "6px",
                  border: `2px solid ${selectedFlag === "green" ? "#22c55e" : "var(--border-color-light)"}`,
                  background:
                    selectedFlag === "green"
                      ? "rgba(34, 197, 94, 0.1)"
                      : "transparent",
                  color:
                    selectedFlag === "green" ? "#16a34a" : "var(--text-muted)",
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                <span
                  style={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    background: "#22c55e",
                  }}
                />
                All Good
              </button>
              <button
                type="button"
                onClick={() => setSelectedFlag("red")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  padding: "5px 12px",
                  borderRadius: "6px",
                  border: `2px solid ${selectedFlag === "red" ? "#ef4444" : "var(--border-color-light)"}`,
                  background:
                    selectedFlag === "red"
                      ? "rgba(239, 68, 68, 0.1)"
                      : "transparent",
                  color:
                    selectedFlag === "red" ? "#dc2626" : "var(--text-muted)",
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                <span
                  style={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    background: "#ef4444",
                  }}
                />
                Needs Review
              </button>
              <button
                type="button"
                onClick={() => setSelectedFlag("purple")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  padding: "5px 12px",
                  borderRadius: "6px",
                  border: `2px solid ${selectedFlag === "purple" ? "#9333ea" : "var(--border-color-light)"}`,
                  background:
                    selectedFlag === "purple"
                      ? "rgba(147, 51, 234, 0.1)"
                      : "transparent",
                  color:
                    selectedFlag === "purple" ? "#7c3aed" : "var(--text-muted)",
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                <Handshake size={12} />
                Agreement Reached
              </button>
            </div>

            {/* Submit */}
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={!text.trim() || submitting}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "6px 16px",
                fontSize: "0.8rem",
              }}
            >
              <Send size={14} />
              {submitting ? "Posting..." : "Post"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
